/**
 * LST Service — CRUD Supabase para o Trabalho Padronizado dos Líderes.
 *
 * Regras do projeto:
 *  - Sempre filtrar por organization_id (multi-tenant / RLS).
 *  - Nunca chamar Supabase direto em componentes.
 */

import { supabase } from '../../../lib/supabaseClient';
import { updateMemberReportsTo } from '../../../services/organizationStructureService';
import type {
  LstCategory,
  LstExecucao,
  LstFrequency,
  LstPeriod,
  LstStatus,
  LstTemplate,
  LstCargo,
  LstSwiChecklistItem,
  LstSwiMode,
  LstActivityKind,
  LstAlertChannel,
  LstRevisaoAta,
  LstMilestone,
  LstLayerMeeting,
  LstLayerAction,
  LstLayerMeetingKey,
} from '../types';

export const LST_TEMPLATES_TABLE = 'lst_templates';
export const LST_EXECUCAO_TABLE = 'lst_execucao';
export const LST_REVISAO_TABLE = 'lst_revisao_ata';
export const LST_MILESTONES_TABLE = 'lst_milestones';
export const LST_LAYER_MEETINGS_TABLE = 'lst_layer_meetings';
/** Bucket de armazenamento reutilizado para anexos das execuções LST. */
export const LST_ANEXO_BUCKET = 'task-attachments';

/** Data ISO de `n` meses atrás (início do dia), no formato YYYY-MM-DD. */
export function monthsAgoISO(n: number, ref: Date = new Date()): string {
  const d = new Date(ref);
  d.setMonth(d.getMonth() - n);
  d.setHours(0, 0, 0, 0);
  return toLocalISODate(d);
}

/** Primeiro dia de cada um dos últimos `count` meses (mais antigo → atual). */
export function lastMonthsKeys(count: number, ref: Date = new Date()): Array<{ key: string; label: string }> {
  const out: Array<{ key: string; label: string }> = [];
  const base = new Date(ref.getFullYear(), ref.getMonth(), 1);
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    out.push({ key, label });
  }
  return out;
}

// ─── Utilitários de data ────────────────────────────────────

/** Data local no formato YYYY-MM-DD (sem deslocamento de fuso). */
export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function todayISO(): string {
  return toLocalISODate(new Date());
}

/** Segunda-feira da semana de `ref` (semana Seg→Dom). */
export function startOfWeek(ref: Date = new Date()): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = domingo
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/** Array com as 7 datas (Seg→Dom) da semana de `ref`. */
export function weekDates(ref: Date = new Date()): string[] {
  const start = startOfWeek(ref);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return toLocalISODate(d);
  });
}

export const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

/** Todas as datas (YYYY-MM-DD) do mês de `ref`. */
export function monthDates(ref: Date = new Date()): string[] {
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const total = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: total }, (_, i) => toLocalISODate(new Date(year, month, i + 1)));
}

/** Índice do dia da semana no padrão Seg=0 … Dom=6. */
export function weekdayIndexMon0(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  const js = new Date(y, m - 1, d).getDay(); // 0=Dom
  return js === 0 ? 6 : js - 1;
}

// ─── Service ────────────────────────────────────────────────

export class LstService {
  /** Lista os padrões ativos de uma organização (ordenados por turno + ordem). */
  static async listTemplates(organizationId: string): Promise<LstTemplate[]> {
    if (!organizationId) return [];
    const { data, error } = await supabase
      .from(LST_TEMPLATES_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      console.warn('[LstService.listTemplates] Falhou:', error.message);
      return [];
    }
    return ((data ?? []) as LstTemplate[]).map(normalizeTemplate);
  }

  /** Execuções de um conjunto de usuários dentro de um intervalo de datas. */
  static async listExecucao(
    organizationId: string,
    userIds: string[],
    startDate: string,
    endDate: string,
  ): Promise<LstExecucao[]> {
    if (!organizationId || userIds.length === 0) return [];
    const { data, error } = await supabase
      .from(LST_EXECUCAO_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .in('user_id', userIds)
      .gte('data', startDate)
      .lte('data', endDate);

    if (error) {
      console.warn('[LstService.listExecucao] Falhou:', error.message);
      return [];
    }
    return (data ?? []).map((row) => normalizeExecucao(row as LstExecucao));
  }

  /**
   * Registra/atualiza a execução de uma rotina (idempotente por template+user+data).
   * Usa upsert sobre o índice único uq_lst_execucao_template_user_data.
   */
  static async upsertExecucao(input: {
    organization_id: string;
    template_id: string;
    user_id: string;
    data: string;
    status: LstStatus;
    justificativa_desvio?: string | null;
    action_id?: string | null;
    observacao?: string | null;
    anexo_url?: string | null;
    indicator_realized_value?: number | null;
  }): Promise<LstExecucao | null> {
    const payload = {
      organization_id: input.organization_id,
      template_id: input.template_id,
      user_id: input.user_id,
      data: input.data,
      status: input.status,
      justificativa_desvio: input.justificativa_desvio ?? null,
      ...(input.action_id ? { action_id: input.action_id } : {}),
      ...(input.observacao !== undefined ? { observacao: input.observacao } : {}),
      ...(input.anexo_url !== undefined ? { anexo_url: input.anexo_url } : {}),
      ...(input.indicator_realized_value !== undefined
        ? { indicator_realized_value: input.indicator_realized_value }
        : {}),
    };

    const { data, error } = await supabase
      .from(LST_EXECUCAO_TABLE)
      .upsert(payload, { onConflict: 'template_id,user_id,data' })
      .select()
      .single();

    if (error) {
      console.error('[LstService.upsertExecucao] Falhou:', error.message);
      return null;
    }
    return normalizeExecucao(data as LstExecucao);
  }

  /**
   * Histórico recente de execuções de uma rotina por um usuário —
   * alimenta a "Memória de Campo" (notas/justificativas anteriores).
   */
  static async listTemplateHistory(
    organizationId: string,
    templateId: string,
    userId: string,
    limit = 5,
  ): Promise<LstExecucao[]> {
    if (!organizationId || !templateId || !userId) return [];
    const { data, error } = await supabase
      .from(LST_EXECUCAO_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .eq('template_id', templateId)
      .eq('user_id', userId)
      .order('data', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('[LstService.listTemplateHistory] Falhou:', error.message);
      return [];
    }
    return (data ?? []).map((row) => normalizeExecucao(row as LstExecucao));
  }

  /**
   * Histórico org-wide de uma rotina (qualquer operador) —
   * garante continuidade do Kaizen entre turnos/camadas.
   */
  static async listTemplateOrgHistory(
    organizationId: string,
    templateId: string,
    limit = 5,
  ): Promise<LstExecucao[]> {
    if (!organizationId || !templateId) return [];
    const { data, error } = await supabase
      .from(LST_EXECUCAO_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .eq('template_id', templateId)
      .order('data', { ascending: false })
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('[LstService.listTemplateOrgHistory] Falhou:', error.message);
      return [];
    }
    return (data ?? []).map((row) => normalizeExecucao(row as LstExecucao));
  }

  /**
   * Upload de foto/anexo de uma execução (auditoria visual 5S/Segurança).
   * Reutiliza o bucket de anexos do projeto; retorna a URL pública.
   */
  static async uploadAnexo(
    organizationId: string,
    templateId: string,
    file: File,
  ): Promise<string | null> {
    return LstService.uploadFile(organizationId, `lst/exec/${templateId}`, file);
  }

  /** Upload do POP visual da SWI (configuração do padrão). */
  static async uploadSwiDocument(
    organizationId: string,
    templateId: string,
    file: File,
  ): Promise<string | null> {
    return LstService.uploadFile(organizationId, `lst/swi/${templateId}`, file);
  }

  private static async uploadFile(
    organizationId: string,
    folder: string,
    file: File,
  ): Promise<string | null> {
    const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${folder}/${organizationId}/${Date.now()}_${sanitized}`;
    const { error } = await supabase.storage
      .from(LST_ANEXO_BUCKET)
      .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
    if (error) {
      console.error('[LstService.uploadFile] Falhou:', error.message);
      return null;
    }
    const { data } = supabase.storage.from(LST_ANEXO_BUCKET).getPublicUrl(path);
    return data.publicUrl ?? null;
  }

  // ── Parametrização (Configuração) ─────────────────────────

  /** Cria uma rotina padrão. */
  static async createTemplate(input: LstTemplateInput): Promise<LstTemplate | null> {
    const { data, error } = await supabase
      .from(LST_TEMPLATES_TABLE)
      .insert(input)
      .select()
      .single();
    if (error) {
      console.error('[LstService.createTemplate] Falhou:', error.message);
      return null;
    }
    return normalizeTemplate(data as LstTemplate);
  }

  /** Atualiza uma rotina padrão. */
  static async updateTemplate(
    id: string,
    patch: Partial<LstTemplateInput>,
  ): Promise<LstTemplate | null> {
    const { data, error } = await supabase
      .from(LST_TEMPLATES_TABLE)
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('[LstService.updateTemplate] Falhou:', error.message);
      return null;
    }
    return normalizeTemplate(data as LstTemplate);
  }

  /** Remove (soft via active=false) uma rotina padrão. */
  static async deactivateTemplate(id: string): Promise<boolean> {
    const { error } = await supabase
      .from(LST_TEMPLATES_TABLE)
      .update({ active: false })
      .eq('id', id);
    if (error) {
      console.error('[LstService.deactivateTemplate] Falhou:', error.message);
      return false;
    }
    return true;
  }

  // ── Ata de Revisão + Milestones ───────────────────────────

  static async listRevisoes(organizationId: string, limit = 20): Promise<LstRevisaoAta[]> {
    if (!organizationId) return [];
    const { data, error } = await supabase
      .from(LST_REVISAO_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      console.warn('[LstService.listRevisoes] Falhou:', error.message);
      return [];
    }
    return (data ?? []) as LstRevisaoAta[];
  }

  static async createRevisaoWithMilestone(input: {
    organization_id: string;
    target_role: LstCargo;
    liderado_user_id?: string | null;
    reviewer_user_id: string;
    notes: string;
    label?: string;
  }): Promise<{ revisao: LstRevisaoAta; milestone: LstMilestone } | null> {
    const { data: revisao, error: revErr } = await supabase
      .from(LST_REVISAO_TABLE)
      .insert({
        organization_id: input.organization_id,
        target_role: input.target_role,
        liderado_user_id: input.liderado_user_id ?? null,
        reviewer_user_id: input.reviewer_user_id,
        notes: input.notes,
      })
      .select()
      .single();
    if (revErr || !revisao) {
      console.error('[LstService.createRevisao] Falhou:', revErr?.message);
      return null;
    }
    const today = todayISO();
    const { data: milestone, error: msErr } = await supabase
      .from(LST_MILESTONES_TABLE)
      .insert({
        organization_id: input.organization_id,
        revisao_id: revisao.id,
        target_role: input.target_role,
        milestone_date: today,
        label: input.label ?? `Revisão ${input.target_role}`,
        notes: input.notes,
        created_by: input.reviewer_user_id,
      })
      .select()
      .single();
    if (msErr || !milestone) {
      console.error('[LstService.createMilestone] Falhou:', msErr?.message);
      return null;
    }
    return { revisao: revisao as LstRevisaoAta, milestone: milestone as LstMilestone };
  }

  static async listMilestones(
    organizationId: string,
    targetRole?: LstCargo,
    startDate?: string,
    endDate?: string,
  ): Promise<LstMilestone[]> {
    if (!organizationId) return [];
    let q = supabase
      .from(LST_MILESTONES_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .order('milestone_date', { ascending: true });
    if (targetRole) q = q.eq('target_role', targetRole);
    if (startDate) q = q.gte('milestone_date', startDate);
    if (endDate) q = q.lte('milestone_date', endDate);
    const { data, error } = await q;
    if (error) {
      console.warn('[LstService.listMilestones] Falhou:', error.message);
      return [];
    }
    return (data ?? []) as LstMilestone[];
  }

  // ── Reuniões de Camada (TPL) ──────────────────────────────

  static async listLayerMeetings(
    organizationId: string,
    filters?: {
      leaderUserId?: string;
      subordinateUserId?: string;
      limit?: number;
    },
  ): Promise<LstLayerMeeting[]> {
    if (!organizationId) return [];
    let q = supabase
      .from(LST_LAYER_MEETINGS_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .order('meeting_date', { ascending: false })
      .order('created_at', { ascending: false });
    if (filters?.leaderUserId) q = q.eq('leader_user_id', filters.leaderUserId);
    if (filters?.subordinateUserId) q = q.eq('subordinate_user_id', filters.subordinateUserId);
    if (filters?.limit) q = q.limit(filters.limit);
    const { data, error } = await q;
    if (error) {
      console.warn('[LstService.listLayerMeetings] Falhou:', error.message);
      return [];
    }
    return (data ?? []).map((row) => normalizeLayerMeeting(row as LstLayerMeeting));
  }

  static async createLayerMeeting(input: {
    organization_id: string;
    meeting_date?: string;
    layer_key: LstLayerMeetingKey;
    leader_user_id: string;
    subordinate_user_id: string;
    leader_cargo: LstCargo;
    subordinate_cargo: LstCargo;
    adherence_pct?: number | null;
    duration_minutes?: number | null;
    sections: Record<string, string>;
    actions: LstLayerAction[];
    evidence_urls?: string[];
    created_by?: string | null;
  }): Promise<LstLayerMeeting | null> {
    const { data, error } = await supabase
      .from(LST_LAYER_MEETINGS_TABLE)
      .insert({
        organization_id: input.organization_id,
        meeting_date: input.meeting_date ?? todayISO(),
        layer_key: input.layer_key,
        leader_user_id: input.leader_user_id,
        subordinate_user_id: input.subordinate_user_id,
        leader_cargo: input.leader_cargo,
        subordinate_cargo: input.subordinate_cargo,
        adherence_pct: input.adherence_pct ?? null,
        duration_minutes: input.duration_minutes ?? null,
        sections: input.sections,
        actions: input.actions,
        evidence_urls: input.evidence_urls ?? [],
        created_by: input.created_by ?? input.leader_user_id,
      })
      .select()
      .single();
    if (error || !data) {
      console.error('[LstService.createLayerMeeting] Falhou:', error?.message);
      return null;
    }
    return normalizeLayerMeeting(data as LstLayerMeeting);
  }

  /** Define o gestor direto/avaliador de um usuário (Cadeia de Ajuda). */
  static async setReportsTo(
    organizationId: string,
    userId: string,
    reportsToUserId: string | null,
  ): Promise<boolean> {
    try {
      await updateMemberReportsTo(organizationId, userId, reportsToUserId);
      return true;
    } catch (err) {
      console.error('[LstService.setReportsTo] Falhou:', (err as Error).message);
      return false;
    }
  }
}

export interface LstTemplateInput {
  organization_id: string;
  target_role: LstCargo;
  scope?: LstTemplateScope;
  assigned_user_id?: string | null;
  title: string;
  description?: string | null;
  frequency: LstFrequency;
  period: LstPeriod;
  scheduled_time?: string | null;
  category?: LstCategory | null;
  is_critical?: boolean;
  sort_order?: number;
  active?: boolean;
  created_by?: string | null;
  swi_text?: string | null;
  swi_doc_url?: string | null;
  swi_checklist?: LstSwiChecklistItem[];
  swi_mode?: LstSwiMode;
  duration_minutes?: number;
  alert_lead_minutes?: 5 | 10 | 15;
  alert_channel?: LstAlertChannel;
  linked_indicator_id?: string | null;
  linked_indicator_name?: string | null;
  linked_indicator_target?: number | null;
  linked_indicator_direction?: 'higher_is_better' | 'lower_is_better';
  activity_kind?: LstActivityKind;
}

/** Normaliza TIME do Postgres ("08:00:00") para HH:MM. */
function normalizeScheduledTime(time: string | null | undefined): string | null {
  if (!time || typeof time !== 'string') return null;
  const parts = time.trim().split(':');
  if (parts.length < 2) return time.trim() || null;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return time.trim() || null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeTemplate(row: LstTemplate): LstTemplate {
  const raw = row.swi_checklist;
  let checklist: LstSwiChecklistItem[] = [];
  if (Array.isArray(raw)) {
    checklist = raw
      .filter((item): item is LstSwiChecklistItem => !!item && typeof item === 'object' && 'text' in item)
      .map((item) => ({
        id: String((item as LstSwiChecklistItem).id ?? `chk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`),
        text: String((item as LstSwiChecklistItem).text ?? ''),
      }))
      .filter((item) => item.text.trim().length > 0);
  }
  const lead = row.alert_lead_minutes;
  const alertLead: 5 | 10 | 15 = lead === 5 || lead === 15 ? lead : 10;
  const channel = row.alert_channel;
  const alertChannel: LstAlertChannel =
    channel === 'push' || channel === 'both' ? channel : 'andon';

  return {
    ...row,
    scope: row.scope === 'user' ? 'user' : 'cargo',
    assigned_user_id: row.assigned_user_id ?? null,
    scheduled_time: normalizeScheduledTime(row.scheduled_time),
    swi_text: row.swi_text ?? null,
    swi_doc_url: row.swi_doc_url ?? null,
    swi_checklist: checklist,
    swi_mode: row.swi_mode ?? 'text',
    duration_minutes: row.duration_minutes ?? 15,
    alert_lead_minutes: alertLead,
    alert_channel: alertChannel,
    linked_indicator_id: row.linked_indicator_id ?? null,
    linked_indicator_name: row.linked_indicator_name ?? null,
    linked_indicator_target:
      row.linked_indicator_target != null ? Number(row.linked_indicator_target) : null,
    linked_indicator_direction:
      row.linked_indicator_direction === 'lower_is_better' ? 'lower_is_better' : 'higher_is_better',
    activity_kind: row.activity_kind === 'reuniao_camada' ? 'reuniao_camada' : 'standard',
  };
}

function normalizeExecucao(row: LstExecucao): LstExecucao {
  return {
    ...row,
    indicator_realized_value:
      row.indicator_realized_value != null ? Number(row.indicator_realized_value) : null,
  };
}

function normalizeLayerMeeting(row: LstLayerMeeting): LstLayerMeeting {
  const sections =
    row.sections && typeof row.sections === 'object' && !Array.isArray(row.sections)
      ? (row.sections as Record<string, string>)
      : {};
  const rawActions = Array.isArray(row.actions) ? row.actions : [];
  const actions: LstLayerAction[] = rawActions
    .filter((item): item is LstLayerAction => !!item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id ?? `act_${Date.now()}`),
      description: String(item.description ?? ''),
      responsible: String(item.responsible ?? ''),
      dueDate: String(item.dueDate ?? ''),
      status: item.status === 'concluida' ? 'concluida' : 'aberta',
    }));
  const evidence_urls = Array.isArray(row.evidence_urls)
    ? row.evidence_urls.map((url) => String(url))
    : [];

  return {
    ...row,
    adherence_pct: row.adherence_pct != null ? Number(row.adherence_pct) : null,
    duration_minutes: row.duration_minutes != null ? Number(row.duration_minutes) : null,
    sections,
    actions,
    evidence_urls,
  };
}
